<script lang="ts">
    import vm, { JourneyNode } from '@journeys/Journeys.viewmodel';

    const component = {
        name: 'Partial_SegmentNodeSettings',
        disposeWatchers: () => {},
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    import { computed, ref, watch, onMounted } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import SelectItemFromList from '@shared/components/SelectItemFromList.vue';
    import { ItemTypes } from '@shared/components/SelectItemFromList';
    import { getList } from '@cwd/Views/Lists/Lists.access';
    import { type List } from '@cwd/Views/Lists/ListsTypes.d';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@cwd/Shared/components/BsAlert.vue';
    import { AlertTypes } from '@cwd/Shared/components/BsAlerts';
    import { JourneyStatus } from '@journeys/Enums';

    const { account, activeJourney } = vm;
    const showSelectorModal = ref(false);
    const selectedList = ref<List | null>(null);
    const loading = ref(true);
    const hadInitialList = ref(false);
    const changedList = ref(false);

    const props = defineProps<{
        data: JourneyNode['data'];
        id: JourneyNode['id'];
    }>();

    onMounted(async () => {
        if (props.data.ListIds && props.data.ListIds.length > 0 && props.data.ListIds[0]) {
            selectedList.value = await getList(props.data.ListIds[0]);
            loading.value = false;
            hadInitialList.value = true;
        } else {
            loading.value = false;
        }

        watch(
            () => props.data.ListIds,
            async (newVal: string[] | undefined) => {
                // if the list is the same, do not reload
                if (selectedList.value && selectedList.value.ListId === newVal?.[0]) {
                    return;
                }

                loading.value = true;

                if (newVal && newVal.length > 0 && newVal[0]) {
                    selectedList.value = await getList(newVal[0]);
                    changedList.value = true;
                }

                loading.value = false;
            }
        );
    });

    const getLists = computed(() => {
        return account.listings.get('/lists');
    });

    const updateListId = (data: any, newId: string) => {
        showSelectorModal.value = false;
        data.ListIds = [newId];
    };

    const journeyIsNotDraft = computed(() => {
        return activeJourney.Status !== JourneyStatus.Draft;
    });

    const showListChangedAlert = computed(() => {
        return changedList.value && hadInitialList.value && journeyIsNotDraft.value;
    });
</script>

<template>
    <div class="m-lg-b">
        <BsAlert
            v-if="showListChangedAlert"
            :dismissible="false"
            :type="AlertTypes.Warning"
            :title="$Resources.JourneyAlertListChangedHeading"
            :content="$Resources.JourneyAlertListChangedBody" />

        <h3 class="h5">{{ $Resources.SelectedListLabel }}:</h3>

        <div class="node-card" v-if="selectedList">
            <h4 class="item-heading">
                <a :href="`/Lists/${selectedList.ListId}`" target="_blank" rel="noopener">
                    {{ selectedList.Name || $Resources.NoTitleLabel }}
                    <small class="glyphicon glyphicon glyphicon-new-window m-xxs-l m-xs-r" aria-hidden="true"></small>
                </a>
            </h4>

            <p v-show="selectedList.Description">
                {{ selectedList.Description }}
            </p>

            <div class="ug-values">
                <div class="ug-value">
                    <span class="ug-value-label">{{ $Resources.ListContactCountLabel }}</span>
                    <span>{{ formatNumericText(selectedList.ContactCount, { precision: 1, largeNumbers: true }) }}</span>
                </div>
            </div>

            <TagsList v-if="selectedList.Tags && selectedList.Tags.length" :tags="selectedList.Tags" :readonly="true"></TagsList>

            <p class="item-meta d-flex gap-xs">
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px">
                    <path
                        d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h357l-80 80H200v560h560v-278l80-80v358q0 33-23.5 56.5T760-120H200Zm280-360ZM360-360v-170l367-367q12-12 27-18t30-6q16 0 30.5 6t26.5 18l56 57q11 12 17 26.5t6 29.5q0 15-5.5 29.5T897-728L530-360H360Zm481-424-56-56 56 56ZM440-440h56l232-232-28-28-29-28-231 231v57Zm260-260-29-28 29 28 28 28-28-28Z" />
                </svg>
                <span>
                    {{ $Resources.LastModifiedLabel }}
                    <time :datetime="selectedList.LastModified">{{ formatDateTime(selectedList.LastModified) }}</time>
                    {{ $Resources.GenericByLabel }}
                    {{ selectedList.ModifiedBy.Name }}
                </span>
            </p>
        </div>

        <div class="form-group">
            <button class="btn btn-default" @click="showSelectorModal = true">
                {{ $Resources.SelectListLabel }}
            </button>
        </div>
    </div>

    <Loading :show="loading" />

    <Modal :title="$Resources.ChooseHeading" :show="showSelectorModal" @close="showSelectorModal = false" size="lg">
        <template #content>
            <SelectItemFromList
                v-if="getLists"
                :items="getLists"
                :type="ItemTypes.Lists"
                @update:selectedId="(newId: string) => updateListId(data, newId)"></SelectItemFromList>
        </template>
    </Modal>
</template>
